
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { getSystemTimezone } from '@/utils/timezoneDetection';

export interface UserProfile {
  id: string;
  display_name: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  christian_tradition?: string;
  preferred_guidance_persona?: 'pastor' | 'theologian' | 'spiritual_psychologist';
  journaling_goal?: string;
  reminder_time?: string;
  words_target: number;
  onboarding_completed: boolean;
  created_at: string;
  updated_at: string;
  invitation_code_used?: string;
  trial_started_at?: string;
  dashboard_tour_completed?: boolean;
  journal_tour_completed?: boolean;
  timezone?: string;
  sunday_sermon_enabled: boolean;
  sunday_sermon_send_hour: number;
  include_deuterocanonical: boolean;
  initial_concerns?: string[];
  personal_intention?: string;
  faith_stage?: string;
  meaningful_scripture?: string;
  session_flow_enabled?: boolean;
  scripture_preference?: string;
  current_streak?: number;
  longest_streak?: number;
  last_journal_date?: string;
  upgrade_modal_shown_at?: string;
  tomorrow_hook?: string;
  tomorrow_hook_generated_at?: string;
  grace_day_used_at?: string;
  last_milestone_reached?: number;
  context_line?: string | null;
  context_line_updated_at?: string | null;
  last_intention?: string | null;
  last_intention_date?: string | null;
}

const isNavigatorLockTimeout = (error: unknown) => {
  const message = typeof error === 'object' && error !== null && 'message' in error
    ? String((error as { message?: string }).message ?? '')
    : '';
  return message.includes('Navigator LockManager lock');
};

let hasLoggedProfileLockTimeout = false;

export const useProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const { user } = useAuth();

  const retryRef = useRef(false);

  const loadProfile = useCallback(async (isRetry = false) => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    // Ensure loading is true while fetching profile for an authenticated user
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        if (isNavigatorLockTimeout(error)) {
          if (!hasLoggedProfileLockTimeout) {
            console.warn('[useProfile] Auth lock timeout while loading profile; retrying silently.');
            hasLoggedProfileLockTimeout = true;
          }
        } else {
          console.warn('[useProfile] Error loading profile (transient):', error);
        }
        // Retry once after a delay instead of showing an error toast
        if (!isRetry && !retryRef.current) {
          retryRef.current = true;
          setTimeout(() => loadProfile(true), 1500);
        }
        return;
      }

      retryRef.current = false;
      setProfile(data ? (data as UserProfile) : null);
    } catch (error) {
      if (isNavigatorLockTimeout(error)) {
        if (!hasLoggedProfileLockTimeout) {
          console.warn('[useProfile] Auth lock timeout while loading profile; retrying silently.');
          hasLoggedProfileLockTimeout = true;
        }
      } else {
        console.warn('[useProfile] Error in loadProfile:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const updatePersonaPreference = async (persona: 'pastor' | 'theologian' | 'spiritual_psychologist') => {
    if (!user) return;

    setUpdating(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ preferred_guidance_persona: persona })
        .eq('id', user.id);

      if (error) throw error;

      setProfile(prev => prev ? { ...prev, preferred_guidance_persona: persona } : null);
      toast.success('Guidance persona updated');
    } catch (error: any) {
      console.error('Error updating persona preference:', error);
      toast.error('Failed to update persona preference');
    } finally {
      setUpdating(false);
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: 'No user found' };

    setUpdating(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .upsert({
          id: user.id,
          ...updates,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'id' })
        .select()
        .maybeSingle();

      if (error) {
        // Retry once on lock timeout
        if (isNavigatorLockTimeout(error)) {
          console.warn('[useProfile] Lock timeout on update, retrying once...');
          await new Promise(r => setTimeout(r, 1000));
          const retry = await supabase
            .from('user_profiles')
            .upsert({ id: user.id, ...updates, updated_at: new Date().toISOString() }, { onConflict: 'id' })
            .select()
            .maybeSingle();
          if (retry.error) {
            console.error('Error upserting profile after retry:', retry.error);
            toast.error('Failed to update profile');
            return { error: retry.error };
          }
          if (retry.data) setProfile(retry.data as UserProfile);
          return { error: null };
        }
        console.error('Error upserting profile:', error);
        toast.error('Failed to update profile');
        return { error };
      }

      // Update local state immediately from returned data
      if (data) {
        setProfile(data as UserProfile);
      } else {
        // Fallback: optimistic update + background refresh
        setProfile(prev => prev ? { ...prev, ...updates } : null);
        loadProfile().catch(() => {});
      }
      return { error: null };
    } catch (error) {
      console.error('Error in updateProfile:', error);
      return { error: 'Update failed' };
    } finally {
      setUpdating(false);
    }
  };

  const completeOnboarding = async (profileData: Partial<UserProfile>) => {
    const result = await updateProfile({
      ...profileData,
      onboarding_completed: true,
    });
    return result;
  };

  useEffect(() => {
    if (user) setLoading(true);
    loadProfile();
  }, [loadProfile]);

  // Auto-update timezone if system timezone has changed (run once)
  const timezoneUpdatedRef = useRef(false);
  useEffect(() => {
    if (!user || !profile || timezoneUpdatedRef.current) return;
    
    const systemTimezone = getSystemTimezone();
    const profileTimezone = profile.timezone || 'America/New_York';
    
    const profileAge = Date.now() - new Date(profile.created_at || 0).getTime();
    const fiveMinutes = 5 * 60 * 1000;
    
    if (systemTimezone !== profileTimezone && profileAge > fiveMinutes) {
      timezoneUpdatedRef.current = true;
      console.log(`🌍 Timezone changed from ${profileTimezone} to ${systemTimezone}, updating...`);
      
      supabase
        .from('user_profiles')
        .update({ timezone: systemTimezone, updated_at: new Date().toISOString() })
        .eq('id', user.id)
        .then(({ error }) => {
          if (error) console.error('Failed to update timezone:', error);
          else setProfile(prev => prev ? { ...prev, timezone: systemTimezone } : null);
        });
    }
  }, [user, profile]);

  return {
    profile,
    loading,
    updating,
    updateProfile,
    updatePersonaPreference,
    completeOnboarding,
    loadProfile,
  };
};
